import HighchartsReact from "highcharts-react-official";
import {useMemo} from "react";

import Highcharts from "@/components/Highcharts";
import {MetricSeries} from "@/lib/types/metricsExplorer.ts";
import {ChartEmptyState} from "@/pages/metricsExplorer/charts/ChartEmptyState.tsx";
import {formatMetricValue} from "@/pages/metricsExplorer/chartSeries.ts";
import {buildScatterPoints, pickScatterPair} from "@/pages/metricsExplorer/chartShapes.ts";

import chartStyles from "./Chart.module.scss";

interface ScatterChartProps {
  series: MetricSeries[];
}

/**
 * Plots two metrics against each other to show whether they move together.
 *
 * Points are paired on their time bucket, so this answers "when this was high, was that
 * high too" rather than merely showing both distributions.
 */
export const ScatterChart = ({series}: ScatterChartProps) => {
  const pair = useMemo(() => pickScatterPair(series), [series]);
  const points = useMemo(() => (pair ? buildScatterPoints(pair[0], pair[1]) : []), [pair]);

  const options = useMemo(() => {
    if (!pair) {
      return null;
    }
    const [xSeries, ySeries] = pair;
    return {
      chart: {type: "scatter", zooming: {type: "xy"}},
      title: {text: ""},
      credits: {enabled: false},
      legend: {enabled: false},
      xAxis: {
        title: {text: xSeries.label},
        labels: {
          formatter: ({value}: {value: number}) => formatMetricValue(value, xSeries.unit),
        },
      },
      yAxis: {
        title: {text: ySeries.label},
        labels: {
          formatter: ({value}: {value: number}) => formatMetricValue(value, ySeries.unit),
        },
      },
      tooltip: {
        outside: true,
        formatter: function (this: {x: number; y: number}): string {
          return (
            `${xSeries.label}: <b>${formatMetricValue(this.x, xSeries.unit)}</b><br/>` +
            `${ySeries.label}: <b>${formatMetricValue(this.y, ySeries.unit)}</b>`
          );
        },
      },
      plotOptions: {scatter: {marker: {radius: 3}}},
      series: [{name: `${ySeries.label} vs ${xSeries.label}`, data: points}],
    };
  }, [pair, points]);

  if (!pair) {
    return (
      <ChartEmptyState
        title="Two series needed"
        message="A scatter compares one metric against another. Add a second series to plot it against."
      />
    );
  }

  if (points.length === 0) {
    return (
      <ChartEmptyState
        title="No overlapping data"
        message="These two series have no time bucket where both reported a value."
      />
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={options} containerProps={{className: chartStyles.container}} />
  );
};
